import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import ConfirmationModal from "../pages/components/general/modals/ConfirmModal";

interface ConfirmationContextType {
  showConfirmation: (options: ConfirmationOptions) => void;
}

interface ConfirmationOptions {
  title: string;
  titleIcon?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  showSuccess?: boolean;
  confirmButtonText?: string;
  confirmButtonVariant?: "primary" | "danger";
  width?: number;
}

export const ConfirmationContext = createContext<
  ConfirmationContextType | undefined
>(undefined);

export function ConfirmationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmationData, setConfirmationData] =
    useState<ConfirmationOptions | null>(null);

  const showConfirmation = useCallback((options: ConfirmationOptions) => {
    setConfirmationData(options);
    setIsOpen(true);
  }, []);

  return (
    <ConfirmationContext.Provider value={{ showConfirmation }}>
      {children}
      {confirmationData && isOpen && (
        <ConfirmationModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          onConfirm={async () => {
            await confirmationData.onConfirm();
            setIsOpen(false);
          }}
          onCancel={confirmationData.onCancel}
          title={confirmationData.title}
          titleIcon={confirmationData.titleIcon}
          subtitle={confirmationData.subtitle}
          showSuccess={confirmationData.showSuccess}
          confirmButtonText={confirmationData.confirmButtonText}
          confirmButtonVariant={confirmationData.confirmButtonVariant}
          width={confirmationData.width}
        >
          {confirmationData.children}
        </ConfirmationModal>
      )}
    </ConfirmationContext.Provider>
  );
}

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error(
      "useConfirmation must be used within a ConfirmationProvider",
    );
  }
  return context;
};
